// @ts-check
// Faixa do bot na conversa (contrato de bots, seções 10 e 22.4): "Bot <nome> conduzindo desde <hora>" com "Assumir
// conversa" (para o bot: a conversa fica com a equipe e o bot fica calado pelo tempo das configurações); com o bot
// parado pela equipe, nada. Sem bot conduzindo, mostra o menu "Iniciar bot" (`BotStartMenu`) quando o número tem bots
// manuais. Montada pela F0 na conversa da 2.0.0 e pela F3 na Caixa, com a mesma assinatura. Publica a conversa em
// `activeConversationState`, para o menu de comandos oferecer "Iniciar o bot" e "Assumir conversa" (bot-commands.js).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { stopConversationBot } from '../../data/bots.js';
import { isTenantModuleEnabled } from '../../data/metadata-store.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatTime } from '../../lib/format.js';
import { BotStartMenu } from './bot-start-menu.js';
import { activeConversationState } from '../command-menu/command-state.js';
import { t } from '../../i18n/index.js';

const SERVICE_DESK_MODULE = 'SERVICE_DESK';

/**
 * @param {{ thread: { id: string, botState?: string | null, bot?: { botId: string, name: string, runId: string, startedAt: string } | null,
 *   channelType?: string, isServiceWindowOpen?: boolean | null } | null, onChanged?: () => void }} props
 */
export function BotConversationBanner({ thread, onChanged }) {
  const [isBusy, setIsBusy] = useState(false);
  const [stoppedRunId, setStoppedRunId] = useState(/** @type {string | null} */ (null));
  const [startableBots, setStartableBots] = useState(/** @type {Array<{ id: string, name: string }>} */ ([]));
  const isEnabled = Boolean(thread) && isTenantModuleEnabled(SERVICE_DESK_MODULE);
  const runningBot = thread && thread.botState === 'BOT' && thread.bot && thread.bot.runId !== stoppedRunId ? thread.bot : null;
  const threadId = thread?.id ?? null;
  useEffect(() => {
    if (!isEnabled || !threadId) return undefined;
    activeConversationState.value = {
      threadId, runningBotName: runningBot?.name ?? null, startableBots: runningBot ? [] : startableBots,
      isServiceWindowOpen: thread?.isServiceWindowOpen ?? null, onChanged,
    };
    return () => {
      if (activeConversationState.peek()?.threadId === threadId) activeConversationState.value = null;
    };
  }, [isEnabled, threadId, runningBot?.runId, runningBot?.name, startableBots, thread?.isServiceWindowOpen, onChanged]);
  if (!thread || !isEnabled) return null;
  const bot = runningBot;
  if (!bot) {
    return thread.channelType && thread.channelType !== 'WHATSAPP' ? null : html`<${BotStartMenu} thread=${thread} onStarted=${onChanged} onBotsLoaded=${setStartableBots} />`;
  }
  const takeOver = async () => {
    setIsBusy(true);
    try {
      await stopConversationBot(thread.id);
      setStoppedRunId(bot.runId);
      showSuccessSnackbar(t('bots.banner.tookOver'));
      onChanged?.();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };
  return html`<div class="bot-conversation-banner" role="status" data-testid="bot-conversation-banner">
    <span class="bot-conversation-banner-icon" aria-hidden="true"><${Icon} name="IconRobot" size=${16} /></span>
    <span class="bot-conversation-banner-text">${t('bots.banner.running', { name: bot.name, time: formatTime(bot.startedAt) })}</span>
    <${Button} title=${t('bots.banner.takeOver')} variant="secondary" size="small" isLoading=${isBusy} onClick=${takeOver} data-testid="bot-take-over" />
  </div>`;
}
